import React, { useEffect, useState } from 'react';
import { Modal, Platform, Pressable, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from './AppText';
import type { AlertButton } from 'react-native';
import { registerAlertHost, type AlertRequest } from './alert';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { radius } from '../theme';

// The web's alert, drawn by the app (see alert.ts). It follows Apple's alert
// guidance: a title, an optional message, and buttons — side by side when
// there are two, with Cancel leading; stacked when there are more, with Cancel
// at the bottom. A destructive button is red. Requests that arrive while one
// is open wait their turn. On iOS and Android this renders nothing.

const DESTRUCTIVE = '#D92D20';
const LINK = '#2878D4';

export function orderButtons(buttons: AlertButton[]): AlertButton[] {
  const cancel = buttons.filter((button) => button.style === 'cancel');
  const rest = buttons.filter((button) => button.style !== 'cancel');
  return buttons.length === 2 ? [...cancel, ...rest] : [...rest, ...cancel];
}

export default function AlertHost() {
  const [queue, setQueue] = useState<AlertRequest[]>([]);
  const colors = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    registerAlertHost((request) => setQueue((current) => [...current, request]));
    return () => registerAlertHost(null);
  }, []);

  const current = queue[0];
  const finish = (button?: AlertButton) => {
    setQueue((rest) => rest.slice(1));
    if (button) button.onPress?.();
    else current?.options?.onDismiss?.();
  };
  const cancelButton = current?.buttons.find((button) => button.style === 'cancel');
  const dismissable = Boolean(cancelButton) || current?.options?.cancelable === true;
  const dismiss = () => { if (dismissable) finish(cancelButton); };

  useEffect(() => {
    if (Platform.OS !== 'web' || !current) return undefined;
    const doc = (globalThis as { document?: { addEventListener(type: string, fn: (event: { key?: string }) => void): void; removeEventListener(type: string, fn: (event: { key?: string }) => void): void } }).document;
    const onKey = (event: { key?: string }) => { if (event.key === 'Escape') dismiss(); };
    doc?.addEventListener('keydown', onKey);
    return () => doc?.removeEventListener('keydown', onKey);
  });

  if (Platform.OS !== 'web' || !current) return null;
  const buttons = current.buttons.length ? orderButtons(current.buttons) : [{ text: t('common.done') } as AlertButton];
  const row = buttons.length === 2;
  return (
    <Modal transparent visible animationType="fade" onRequestClose={dismiss}>
      <Pressable testID="alert-host-backdrop" style={styles.backdrop} onPress={dismiss} accessible={false}>
        <Pressable testID="alert-host" style={[styles.card, { backgroundColor: colors.white }]} onPress={() => {}}
          accessibilityRole="alert" accessibilityViewIsModal>
          <Text style={[styles.title, { color: colors.black }]}>{current.title}</Text>
          {current.message ? <Text style={[styles.message, { color: colors.gray600 }]}>{current.message}</Text> : null}
          <View style={[styles.buttons, row && styles.row, { borderTopColor: colors.gray200 }]}>
            {buttons.map((button, index) => (
              <TouchableOpacity key={`${button.text}-${index}`} testID={`alert-host-button-${index}`} accessibilityRole="button"
                onPress={() => finish(button)}
                style={[styles.button, row && styles.rowButton, row ? index > 0 && [styles.rowDivider, { borderLeftColor: colors.gray200 }]
                  : index > 0 && [styles.stackDivider, { borderTopColor: colors.gray200 }]]}>
                <Text style={[styles.buttonText, {
                  color: button.style === 'destructive' ? DESTRUCTIVE : button.style === 'cancel' ? colors.black : LINK,
                  fontWeight: button.style === 'cancel' ? '600' : '400',
                }]}>{button.text}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.35)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: { width: '100%', maxWidth: 300, borderRadius: radius.card, overflow: 'hidden' },
  title: { fontSize: 17, fontWeight: '600', textAlign: 'center', paddingHorizontal: 16, paddingTop: 18 },
  message: { fontSize: 14, lineHeight: 19, textAlign: 'center', paddingHorizontal: 16, paddingTop: 6 },
  buttons: { marginTop: 18, borderTopWidth: StyleSheet.hairlineWidth },
  row: { flexDirection: 'row' },
  button: { minHeight: 44, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 8 },
  rowButton: { flex: 1 },
  rowDivider: { borderLeftWidth: StyleSheet.hairlineWidth },
  stackDivider: { borderTopWidth: StyleSheet.hairlineWidth },
  buttonText: { fontSize: 17, textAlign: 'center' },
});
